import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { ArchiveIcon, BeakerIcon, FileIcon } from '@primer/octicons-react';

import usePageTitle from '../../../utils/hooks/use_page_title';

import VersionIcon from '../../common/icons/VersionIcon';

import ClipboardLabel from '../../common/ClipboardLabel/ClipboardLabel';
import CollapsibleCardView from '../../common/CardView/CollapsibleCardView';
import ErrorSlate from '../../ErrorSlate/ErrorSlate';
import HeadingLabel from '../../common/Labels/HeadingLabel';
import Icon from '../../common/icons/Icon';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import ModalOverlay from '../../common/ModalOverlay/ModalOverlay';
import SubHeadingLabel from '../../common/Labels/SubHeadingLabel';

import PageNotFound from '../../pages/PageNotFound';

import { useRedirectOnWorkspaceChange } from '../../../utils/hooks/use_redirect_on_value_change';
import useWorkspacePermissions from '../../../utils/hooks/use_workspace_permissions';

import PageLayout from '../../pages/PageLayout';
import PrimaryContent from '../../pages/layouts/PrimaryContent';

import { OrchestrationDataProvider, useOrchestrationData } from '../OrchestrationDataContext/OrchestrationDataContext';
import OrchestrationStatusBadge from '../OrchestrationStatusBadge/OrchestrationStatusBadge';
import OrchestrationTimeline from '../OrchestrationTimeline/OrchestrationTimeline';

import { TaskListDataContextProvider, useTaskListData } from '../TaskListContext/TaskListContext';
import { TaskRunListContextProvider } from '../TaskRunListContext/TaskRunListContext';
import { TaskCreationContextProvider } from '../TaskCreationContext/TaskCreationContext';

import NoTasksSlate from '../TaskBlankSlate/TaskBlankSlate';
import TaskCreationModal from '../TaskCreationModal/TaskCreationModal';
import TaskListTable from '../TaskListTable/TaskListTable';
import TaskRunFilterModal from '../TaskRunFilterModal/TaskRunFilterModal';
import TaskRunListTable from '../TaskRunListTable/TaskRunListTable';

import ButtonTabGroup from '../../common/ButtonTabGroup/ButtonTabGroup';
import FilterCountButton from '../../admin/FilterCountButton';

import {
  ALL_TIME,
  RELATIVE_TIME_RANGES,
  TASK_RUN_STATUS,
} from '../../admin/orchestrations/OrchestrationTableFilterModal/OrchestrationTableFilterModal';
import { getTimeRange, isDateBetweenDates } from '../../../utils/date_utils';

const ContextualTaskListTable = () => {
  const { taskListData, loading, error, refetch: refetchTasks } = useTaskListData();

  return (
    <div className="mt-2 bg-white border border-border-light rounded relative">
      {
        loading && (
          <LoadingSpinner animationKey="task-list-spinner" size="medium" useOverlay />
        )
      }
      {
        error ? (
          <ErrorSlate
            errorText="Something went wrong loading your tasks"
          />
        ) : !taskListData?.length ? (
          <NoTasksSlate />
        ) : (
          <TaskListTable taskListData={taskListData} refetchTasks={refetchTasks} />
        )
      }
    </div>
  )
}

const ContextualTaskRunListTable = ({ filter = {} }) => {
  const { taskListData, loading, error } = useTaskListData();

  const [allTaskRuns, setAllTaskRuns] = useState([])

  const filteredTimeRange = filter?.timeRange ? getTimeRange(filter?.timeRange) : null;
  const filteredStatus = filter?.taskRunStatus.toLowerCase();
  const isFilteringTimeRange = filter?.timeRange?.id !== 'all-time';

  useEffect(() => {
    if (!taskListData) return
  
    const getTaskRuns = taskListData?.map(data => {
      const lastRuns = data?.last_runs || [];
      return lastRuns.map((run) => ({
        ...run,
        id: data?.id,
        name: data?.name,
        run_type: data?.run_type,
        lastRuns: data?.last_runs,
        schedule: data?.schedule
      }))
    }).flat();

    const filteredTaskRuns = getTaskRuns.filter((taskRun) => {
      const creationTime = taskRun.created_at;
      const status = taskRun.status;

      const isFilteringStatus = filteredStatus !== 'all';

      return (!isFilteringStatus || status.toLowerCase() === filteredStatus) && (!isFilteringTimeRange || isDateBetweenDates(creationTime, filteredTimeRange[0], filteredTimeRange[1]))
    })

    setAllTaskRuns(filteredTaskRuns)
  }, [taskListData, filteredStatus, filteredTimeRange, isFilteringTimeRange])
  
  return (
    <div className="mt-2 bg-white border border-border-light rounded relative">
      {
        loading && (
          <LoadingSpinner animationKey="task-list-spinner" size="medium" useOverlay />
        )
      }
      {
        error ? (
          <ErrorSlate
            errorText="Something went wrong loading your tasks"
          />
        ) : !allTaskRuns?.length ? (
          <NoTasksSlate />
        ) : (
          <TaskRunListTable allTaskRuns={allTaskRuns} />
        )
      }
    </div>
  )
}

const DEFAULT_FILTER = {
  timeRange: RELATIVE_TIME_RANGES[0],
  taskRunStatus: TASK_RUN_STATUS.All,
};

export const OrchestrationDetailPage = ({
  orchestration,
  loading,
  error,
}) => {
  const routeParams = useParams();
  const {
    focusedTab,
    durationId,
    fromDate: fromMillis,
    toDate: toMillis,
    taskRunStatus,
  } = routeParams;

  const initialFilter = { ...DEFAULT_FILTER };
  if (taskRunStatus) {
    initialFilter.taskRunStatus = taskRunStatus;
  }

  if (durationId?.length) {
    initialFilter.timeRange = RELATIVE_TIME_RANGES.find(({ id }) => id === durationId);
  } else if (fromMillis !== undefined && fromMillis !== null) {
    initialFilter.timeRange = {
      type: 'custom',
      fromDate: new Date(parseInt(fromMillis)),
      toDate: new Date(parseInt(toMillis)),
    }
  }

  const { canModify } = useWorkspacePermissions();
  const [isTaskCreationModalOpen, setTaskCreationModalOpen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(focusedTab === 'taskRuns' ? 'Task Runs' : 'Task');

  const isTaskRunsTab = selectedTab === 'Task Runs';

  const [filterObj, setFilterObj] = useState(initialFilter);
  let activeFilterCount = 0;
  if (filterObj.timeRange?.type !== ALL_TIME) {
    activeFilterCount += 1;
  }

  if (filterObj.taskRunStatus !== TASK_RUN_STATUS.All) {
    activeFilterCount += 1;
  }

  const onFilterConfirm = (filter) => {
    setFilterObj(filter);
    setShowFilterModal(false);
  }

  const onFilterClear = () => {
    setFilterObj(DEFAULT_FILTER);
    setShowFilterModal(false);
  }

  const TASK_DETAIL_TABS = useMemo(() => ({
    Task: 'Task',
    TaskRuns: 'Task Runs'
  }), [])

  const taskTabs = useMemo(() => {
    const options = Object.values(TASK_DETAIL_TABS)
    return options.map(option => ({
      value: option
    }))
  }, [TASK_DETAIL_TABS])

  usePageTitle(`${orchestration?.name ? orchestration.name : 'Workload'} | Wallaroo`);

  const orchName = orchestration?.name ? orchestration.name : '---';
  const orchSha = orchestration?.sha;
  if (!loading && !error && !orchestration) {
    return <PageNotFound orchestrationID={routeParams.id} />;
  }

  // TODO use isTaskCreationModalOpen to display Task Creation Modal
  return (
    <div className="w-full mb-16">
      <div className="bg-brand border-t-2 border-border-brand text-white flex justify-center items-center">
        <div className="px-16 py-4 w-full flex-grow flex flex-col items-start">
          <div className="flex items-center">
            <Icon icon={BeakerIcon} size={48} />
            <HeadingLabel
              className="ml-2 mt-1"
              size="4xl"
              italic={!orchestration?.name}
              bold={!!orchestration?.name}
            >
              {orchName}
            </HeadingLabel>
          </div>
          <div className="ml-6 my-2 flex items-center">
            <Icon icon={VersionIcon} size={32} useTailwindSizing />
            <SubHeadingLabel className="ml-2" size="2xl">
              {routeParams?.id}
            </SubHeadingLabel>
          </div>
          {
            !orchestration ? (
              error ? (
                <SubHeadingLabel className="ml-2 mt-2" size="xl">
                  Something went wrong loading this workload
                </SubHeadingLabel>
              ) : (
                <div className="ml-2 mt-2 flex flex-row">
                  <SubHeadingLabel size="xl">
                    Loading Workload Data
                  </SubHeadingLabel>
                  <LoadingSpinner className="ml-2" animationKey="orchestration-detail-loading-spinner" />
                </div>
              )
            ) : (
              <>
                <div className="ml-6 mt-1 mb-2 flex-grow flex-wrap flex flex-row items-center justify-center">
                  <div className="flex items-center justify-center">
                    <Icon icon={FileIcon} size={32} />
                    <SubHeadingLabel bold className="ml-2" size="xl">
                      {orchestration.file_name}
                    </SubHeadingLabel>
                  </div>
                  {
                    orchSha && (
                      <ClipboardLabel
                        className="ml-2"
                        iconSize={12}
                        labelValue={orchSha}
                        displayLabel={orchSha}
                        LabelComponent={SubHeadingLabel}
                        showClipboardIconOnHover
                      />
                    )
                  }
                </div>
                <OrchestrationStatusBadge status={orchestration?.status} />
              </>
            )
          }
        </div>
      </div>

      {!error && (
        <PageLayout>
          <PrimaryContent>
            <div className="mb-2">
              <OrchestrationTimeline
                orchestrationHistory={[orchestration]}
                error={error}
                loading={loading}
                timelineTitle={orchName}
                disableScrollFading
              />
            </div>
            <TaskListDataContextProvider orchId={orchestration?.id} orchSha={orchestration?.sha}>
              <TaskRunListContextProvider taskRunFilter={filterObj}>
                <CollapsibleCardView
                  headerText={`Workload Log: ${orchName}`}
                  icon={ArchiveIcon}
                  quickActionsArray={[{
                    label: 'Create Task',
                    onClick: () => setTaskCreationModalOpen(true),
                    disabled: !canModify,
                    disabledText: 'Insufficient workspace permissions',
                  }]}
                >
                  <div className="flex justify-between w-full">
                    <ButtonTabGroup
                      selectedOption={selectedTab}
                      options={taskTabs}
                      onItemSelected={setSelectedTab}
                    />
                    {
                      isTaskRunsTab && (
                        <FilterCountButton
                          activeFilters={activeFilterCount}
                          onClick={() => setShowFilterModal(true)}
                        >
                          Filter
                        </FilterCountButton>
                      )
                    }
                  </div>
                  {
                    !isTaskRunsTab ? <ContextualTaskListTable /> : <ContextualTaskRunListTable filter={filterObj} />
                  }
                </CollapsibleCardView>
              </TaskRunListContextProvider>
            </TaskListDataContextProvider>
          </PrimaryContent>
        </PageLayout>
      )}
      <TaskCreationContextProvider
        orchestrationId={orchestration?.id}
        onClose={() => setTaskCreationModalOpen(false)}
      >
        {
          isTaskCreationModalOpen && (
            <ModalOverlay
              animationKey="task-creation-modal"
              active={isTaskCreationModalOpen}
              onModalToggle={() => setTaskCreationModalOpen(false)}
            >
              <div style={{ width: '800px', height: '600px' }}>
                <TaskCreationModal
                  onClose={() => setTaskCreationModalOpen(false)}
                />
              </div>
            </ModalOverlay>
          )
        }
      </TaskCreationContextProvider>
      {
        showFilterModal && (
          <TaskRunFilterModal
            filter={filterObj}
            showDialog={showFilterModal}
            onDialogToggle={() => setShowFilterModal(false)}
            onFilterConfirm={onFilterConfirm}
            onClear={onFilterClear}
          />
        )
      }
    </div>
  );
};

const ContextualOrchestrationDetailPage = () => {
  const {
    orchestration,
    loading,
    error,
  } = useOrchestrationData();

  useRedirectOnWorkspaceChange('/models')

  return (
    <OrchestrationDataProvider>
      <OrchestrationDetailPage
        orchestration={orchestration}
        loading={loading}
        error={error}
      />
    </OrchestrationDataProvider>
  );
}

const WrappedOrchestrationDetailPage = () => (
  <OrchestrationDataProvider>
    <ContextualOrchestrationDetailPage />
  </OrchestrationDataProvider>
)

export default WrappedOrchestrationDetailPage;
